import {
  EGGS,
  EGG_BY_ID,
  ENEMY_BY_ID,
  ITEMS,
  ITEM_BY_ID,
  UPGRADES,
  UPGRADE_BY_ID,
  WEAPONS,
  WEAPON_BY_ID,
} from "./content";
import { nextRandom, normalizeSeed, randomBetween } from "./random";
import {
  challengeTypeForWave,
  enemyWaveScaling,
  getWaveSpawnProfile,
  waveDurationSeconds,
} from "./waves";
import {
  TICK_RATE,
  TICK_SECONDS,
  type BattleConfig,
  type BattleEvent,
  type BattleInput,
  type BattleState,
  type Enemy,
  type EnemyKind,
  type Pickup,
  type LootReward,
  type ShopOffer,
  type ShellBonuses,
  type StatModifiers,
  type UpgradeOffer,
  type Vec2,
  type WeaponInstance,
} from "./types";

export const DEFAULT_BATTLE_CONFIG: BattleConfig = {
  waveSeconds: 30,
  enemyHealthMultiplier: 1,
  enemySpeedMultiplier: 1,
  enemyDamageMultiplier: 1,
  dangerLevel: 0,
  dynamicWaveDuration: true,
};

const MAX_ENEMIES = 100;
const MAX_PROJECTILES = 320;
const MAX_PICKUPS = 220;
const DEFAULT_WEAPON_SLOT_LIMIT = 6;

export function createBattle(
  seed = Date.now(),
  config: BattleConfig = DEFAULT_BATTLE_CONFIG,
  eggId = "fresh-egg",
): BattleState {
  const normalizedSeed = normalizeSeed(seed);
  const egg = EGG_BY_ID.get(eggId) ?? EGGS[0];
  if (!egg) throw new Error("Eggato requires at least one egg character.");
  const requestedWeapon = config.startingWeaponId
    ? WEAPON_BY_ID.get(config.startingWeaponId)
    : undefined;
  const startingWeapon =
    requestedWeapon &&
    (!config.unlockedWeaponIds ||
      config.unlockedWeaponIds.includes(requestedWeapon.id))
      ? requestedWeapon
      : WEAPON_BY_ID.get(egg.startingWeaponId);
  if (!startingWeapon) {
    throw new Error("Eggato requires a valid starting weapon.");
  }
  const dangerLevel = config.dangerLevel ?? 0;
  const waveDuration = waveDurationTicks(1, config);
  const state: BattleState = {
    seed: normalizedSeed,
    randomState: normalizedSeed,
    tick: 0,
    nextEntityId: 1,
    arena: { width: 1600, height: 900 },
    phase: "combat",
    wave: 1,
    waveTicksRemaining: waveDuration,
    waveDurationTicks: waveDuration,
    spawnTicks: getWaveSpawnProfile(1).openingDelayTicks,
    treeSpawnTicks: 360,
    pendingLevelUps: 0,
    upgradeChoices: [],
    upgradeRefreshes: 0,
    shopOffers: [],
    shopRefreshes: 0,
    eggId: egg.id,
    dangerLevel,
    endless: config.endless ?? false,
    hordeWave: challengeTypeForWave(normalizedSeed, dangerLevel, 1) === "horde",
    baggedMaterials: 0,
    lootQueue: [],
    specialEnemySpawned: false,
    weaponClassBonuses: {},
    availableWeaponIds: config.unlockedWeaponIds?.length
      ? [...new Set([...config.unlockedWeaponIds, startingWeapon.id])]
      : WEAPONS.map((entry) => entry.id),
    availableItemIds: config.unlockedItemIds?.length
      ? [...new Set(config.unlockedItemIds)]
      : ITEMS.map((entry) => entry.id),
    weapons: [
      {
        definitionId: startingWeapon.id,
        tier: 1,
        cooldownTicks: 10,
      },
    ],
    items: [],
    player: {
      position: { x: 800, y: 450 },
      velocity: { x: 0, y: 0 },
      radius: 27,
      health: 100,
      maxHealth: 100,
      speed: 310,
      armor: 4,
      damageMultiplier: 1,
      meleeDamage: 0,
      rangedDamage: 0,
      elementalDamage: 0,
      attackSpeedMultiplier: 1,
      critChance: 0,
      lifeStealChance: 0,
      projectileSpeedMultiplier: 1,
      range: 0,
      pickupRadius: 150,
      dodgeChance: 0,
      regenerationPerSecond: 0,
      luck: 0,
      harvesting: 0,
      engineering: 0,
      knockback: 0,
      regenerationProgress: 0,
      bonusProjectiles: egg.bonusProjectiles ?? 0,
      weaponSlotLimit: egg.weaponSlotLimit ?? DEFAULT_WEAPON_SLOT_LIMIT,
      invulnerableTicks: 0,
      level: 1,
      experience: 0,
      experienceToNext: 18,
      materials: 0,
      materialsCollected: 0,
      kills: 0,
    },
    enemies: [],
    projectiles: [],
    pickups: [],
  };
  applyModifiers(state, egg.modifiers);
  syncWeaponClassBonuses(state);
  return state;
}

export function setPaused(state: BattleState, paused: boolean): BattleEvent[] {
  if (paused && state.phase === "combat") {
    state.phase = "paused";
    return [{ type: "phase-change", phase: "paused" }];
  }
  if (!paused && state.phase === "paused") {
    state.phase = "combat";
    return [{ type: "phase-change", phase: "combat" }];
  }
  return [];
}

export function getShellBonuses(state: BattleState): ShellBonuses {
  const ratio = state.player.health / Math.max(1, state.player.maxHealth);
  if (ratio > 0.66) {
    return {
      stage: "smooth",
      armor: 10,
      attackSpeedMultiplier: 1,
      damageMultiplier: 1,
    };
  }
  if (ratio > 0.33) {
    return {
      stage: "hairline",
      armor: -5,
      attackSpeedMultiplier: 1.12,
      damageMultiplier: 1,
    };
  }
  return {
    stage: "deep",
    armor: -10,
    attackSpeedMultiplier: 1.25,
    damageMultiplier: 1.15,
  };
}

export function stepBattle(
  state: BattleState,
  input: BattleInput,
  config: BattleConfig = DEFAULT_BATTLE_CONFIG,
): BattleEvent[] {
  if (state.phase !== "combat") return [];

  const events: BattleEvent[] = [];
  state.tick += 1;
  state.waveTicksRemaining -= 1;
  if (
    state.waveTicksRemaining > 0 &&
    state.waveTicksRemaining <= TICK_RATE * 5 &&
    state.waveTicksRemaining % TICK_RATE === 0
  ) {
    events.push({
      type: "countdown",
      seconds: state.waveTicksRemaining / TICK_RATE,
    });
  }

  updatePlayer(state, input);
  spawnEnemies(state, config, events);
  spawnTrees(state, config);
  fireWeapons(state, events);
  updateProjectiles(state);
  updateEnemies(state);
  resolveProjectileHits(state, events);
  resolveEnemyContact(state, config, events);
  updatePickups(state, events);
  regeneratePlayer(state);

  if (state.player.invulnerableTicks > 0) {
    state.player.invulnerableTicks -= 1;
  }
  for (const enemy of state.enemies) {
    if (enemy.hitFlashTicks > 0) enemy.hitFlashTicks -= 1;
  }

  if (state.player.health <= 0) {
    state.player.health = 0;
    state.phase = "gameover";
    events.push({ type: "phase-change", phase: "gameover" });
  } else if (state.waveTicksRemaining <= 0 || finalBossesDefeated(state)) {
    finishWave(state, events);
  }

  return events;
}

function finalBossesDefeated(state: BattleState): boolean {
  if (state.endless || state.wave !== 20 || !state.specialEnemySpawned) {
    return false;
  }
  return !state.enemies.some(
    (enemy) => ENEMY_BY_ID.get(enemy.kind)?.rank === "boss",
  );
}

export function chooseUpgrade(
  state: BattleState,
  offerId: number | string,
): BattleEvent[] {
  if (state.phase !== "levelup") return [];
  const offer =
    typeof offerId === "number"
      ? state.upgradeChoices.find((entry) => entry.id === offerId)
      : state.upgradeChoices.find((entry) => entry.definitionId === offerId);
  if (!offer) return [];
  const upgrade = UPGRADE_BY_ID.get(offer.definitionId);
  if (!upgrade) return [];

  applyModifiers(state, scaleModifiers(upgrade.modifiers, upgradeTierPower(offer.tier)));
  state.pendingLevelUps = Math.max(0, state.pendingLevelUps - 1);
  const events: BattleEvent[] = [];
  if (state.pendingLevelUps > 0) {
    state.upgradeChoices = generateUpgradeOffers(state);
  } else {
    openShop(state);
    events.push({ type: "phase-change", phase: "shop" });
  }
  return events;
}

export function refreshUpgrades(state: BattleState): boolean {
  if (state.phase !== "levelup") return false;
  const cost = upgradeRefreshCost(state);
  if (state.player.materials < cost) return false;
  state.player.materials -= cost;
  state.upgradeRefreshes += 1;
  state.upgradeChoices = generateUpgradeOffers(state);
  return true;
}

export function upgradeRefreshCost(state: BattleState): number {
  if (state.upgradeRefreshes < itemEffectTotal(state, "free-rerolls")) return 0;
  return Math.max(1, Math.floor(state.wave * 0.75) + Math.max(1, Math.floor(state.wave * 0.4)) * state.upgradeRefreshes);
}

export function buyShopOffer(
  state: BattleState,
  offerId: number,
): BattleEvent[] {
  if (!canBuyShopOffer(state, offerId)) return [];
  const offer = state.shopOffers.find((entry) => entry.id === offerId);
  if (!offer) return [];

  if (offer.kind === "weapon") {
    const definition = WEAPON_BY_ID.get(offer.definitionId);
    if (!definition) return [];
    state.player.materials -= offer.price;
    offer.sold = true;
    const mergeResult =
      state.weapons.length >= state.player.weaponSlotLimit
        ? mergePurchasedWeapon(state, offer.definitionId, offer.tier)
        : null;
    if (!mergeResult) {
      state.weapons.push({
        definitionId: offer.definitionId,
        tier: offer.tier,
        cooldownTicks: 12,
      });
    }
    syncWeaponClassBonuses(state);
    return mergeResult
      ? [
          {
            type: "weapon-merged",
            name: definition.name,
            tier: mergeResult.tier,
          },
        ]
      : [{ type: "purchase", name: definition.name }];
  }

  const definition = ITEM_BY_ID.get(offer.definitionId);
  if (!definition) return [];
  state.player.materials -= offer.price;
  offer.sold = true;
  state.items.push(definition.id);
  applyModifiers(state, definition.modifiers);
  return [{ type: "purchase", name: definition.name }];
}

export function canBuyShopOffer(
  state: BattleState,
  offerId: number,
): boolean {
  if (state.phase !== "shop") return false;
  const offer = state.shopOffers.find((entry) => entry.id === offerId);
  if (!offer || offer.sold || state.player.materials < offer.price) return false;
  if (offer.kind === "weapon") {
    return (
      WEAPON_BY_ID.has(offer.definitionId) &&
      canTakeWeapon(state, offer.definitionId, offer.tier)
    );
  }
  return ITEM_BY_ID.has(offer.definitionId);
}

export function recycleWeapon(
  state: BattleState,
  weaponIndex: number,
): BattleEvent[] {
  if (
    state.phase !== "shop" ||
    state.weapons.length <= 1 ||
    !Number.isInteger(weaponIndex)
  ) {
    return [];
  }
  const weapon = state.weapons[weaponIndex];
  if (!weapon) return [];
  const definition = WEAPON_BY_ID.get(weapon.definitionId);
  if (!definition) return [];

  const value = weaponRecycleValue(weapon);
  state.weapons.splice(weaponIndex, 1);
  state.player.materials += value;
  syncWeaponClassBonuses(state);
  return [{ type: "weapon-recycled", name: definition.name, value }];
}

export function canCombineWeapon(
  state: BattleState,
  weaponIndex: number,
): boolean {
  if (state.phase !== "shop" || !Number.isInteger(weaponIndex)) return false;
  const weapon = state.weapons[weaponIndex];
  if (!weapon || weapon.tier >= 4) return false;
  return state.weapons.some(
    (candidate, index) =>
      index !== weaponIndex &&
      candidate.definitionId === weapon.definitionId &&
      candidate.tier === weapon.tier,
  );
}

export function combineWeapon(
  state: BattleState,
  weaponIndex: number,
): BattleEvent[] {
  if (!canCombineWeapon(state, weaponIndex)) return [];
  const weapon = state.weapons[weaponIndex];
  if (!weapon) return [];
  const matchIndex = state.weapons.findIndex(
    (candidate, index) =>
      index !== weaponIndex &&
      candidate.definitionId === weapon.definitionId &&
      candidate.tier === weapon.tier,
  );
  if (matchIndex < 0) return [];
  const match = state.weapons[matchIndex];
  if (!match) return [];

  state.weapons.splice(matchIndex, 1);
  weapon.tier = Math.min(4, weapon.tier + 1) as WeaponInstance["tier"];
  weapon.cooldownTicks = Math.min(weapon.cooldownTicks, match.cooldownTicks);
  syncWeaponClassBonuses(state);
  const definition = WEAPON_BY_ID.get(weapon.definitionId);
  return definition
    ? [{ type: "weapon-merged", name: definition.name, tier: weapon.tier }]
    : [];
}

export function weaponRecycleValue(weapon: WeaponInstance): number {
  const definition = WEAPON_BY_ID.get(weapon.definitionId);
  if (!definition) return 0;
  return Math.max(1, Math.floor(definition.basePrice * Math.pow(2, weapon.tier - 1) * 0.25));
}

export function refreshShop(state: BattleState): boolean {
  if (state.phase !== "shop") return false;
  const cost = shopRefreshCost(state);
  if (state.player.materials < cost) return false;
  state.player.materials -= cost;
  state.shopRefreshes += 1;
  generateShopOffers(state, true);
  return true;
}

export function shopRefreshCost(state: BattleState): number {
  if (state.shopRefreshes < itemEffectTotal(state, "free-rerolls")) return 0;
  return Math.min(20, 2 + state.shopRefreshes * 2 + Math.floor(state.wave / 4));
}

export function toggleShopOfferLock(
  state: BattleState,
  offerId: number,
): boolean {
  if (state.phase !== "shop") return false;
  const offer = state.shopOffers.find((entry) => entry.id === offerId);
  if (!offer || offer.sold) return false;
  offer.locked = !offer.locked;
  return offer.locked;
}

export function leaveShop(
  state: BattleState,
  config: BattleConfig = DEFAULT_BATTLE_CONFIG,
): BattleEvent[] {
  if (state.phase !== "shop") return [];
  if (state.wave >= 20 && !state.endless) {
    state.phase = "victory";
    return [{ type: "phase-change", phase: "victory" }];
  }

  state.wave += 1;
  const waveDuration = waveDurationTicks(state.wave, config);
  state.waveTicksRemaining = waveDuration;
  state.waveDurationTicks = waveDuration;
  state.spawnTicks = getWaveSpawnProfile(state.wave).openingDelayTicks;
  state.treeSpawnTicks = 260;
  state.enemies = [];
  state.projectiles = [];
  state.pickups = [];
  state.shopRefreshes = 0;
  state.upgradeRefreshes = 0;
  state.specialEnemySpawned = false;
  state.hordeWave =
    challengeTypeForWave(state.seed, state.dangerLevel, state.wave) === "horde";
  state.phase = "combat";
  return [
    { type: "wave-start", wave: state.wave },
    { type: "phase-change", phase: "combat" },
  ];
}

export function continueEndless(
  state: BattleState,
  config: BattleConfig = DEFAULT_BATTLE_CONFIG,
): BattleEvent[] {
  if (state.phase !== "victory" || state.wave !== 20) return [];
  state.endless = true;
  state.phase = "shop";
  return leaveShop(state, config);
}

function updatePlayer(state: BattleState, input: BattleInput): void {
  const direction = normalized({ x: input.moveX, y: input.moveY });
  state.player.velocity.x = direction.x * state.player.speed;
  state.player.velocity.y = direction.y * state.player.speed;
  state.player.position.x = clamp(
    state.player.position.x + state.player.velocity.x * TICK_SECONDS,
    state.player.radius,
    state.arena.width - state.player.radius,
  );
  state.player.position.y = clamp(
    state.player.position.y + state.player.velocity.y * TICK_SECONDS,
    state.player.radius,
    state.arena.height - state.player.radius,
  );
}

function spawnEnemies(
  state: BattleState,
  config: BattleConfig,
  events: BattleEvent[],
): void {
  const progress =
    1 - state.waveTicksRemaining / Math.max(1, state.waveDurationTicks);
  spawnSpecialEnemy(state, config, events);

  state.spawnTicks -= 1;
  if (state.spawnTicks > 0 || state.enemies.length >= MAX_ENEMIES) return;

  const profile = getWaveSpawnProfile(state.wave);
  const spawnCount = Math.min(
    state.hordeWave ? 10 : 7,
    profile.groupSize +
      (progress > 0.68 ? profile.lateGroupBonus : 0) +
      (state.hordeWave ? 3 : 0),
  );
  const anchor = createSpawnAnchor(state);
  for (let index = 0; index < spawnCount; index += 1) {
    if (state.enemies.length >= MAX_ENEMIES) break;
    state.enemies.push(
      createEnemy(
        state,
        chooseEnemyKind(state, progress),
        config,
        anchor,
      ),
    );
  }

  const interval = Math.round(
    profile.intervalStartTicks +
      (profile.intervalEndTicks - profile.intervalStartTicks) * progress,
  );
  state.spawnTicks = Math.max(3, Math.round(interval * (state.hordeWave ? 0.58 : 1)));
}

function chooseEnemyKind(state: BattleState, progress: number): EnemyKind {
  const profile = getWaveSpawnProfile(state.wave);
  const candidates =
    progress > 0.58 ? profile.lateEnemyPool : profile.enemyPool;
  const bias = Math.pow(nextRandom(state), 0.72);
  const index = Math.min(
    candidates.length - 1,
    Math.floor(bias * candidates.length),
  );
  return candidates[index] ?? "mite";
}

function createSpawnAnchor(state: BattleState): Vec2 {
  const padding = 48;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const edge = Math.floor(nextRandom(state) * 4);
    let x = randomBetween(state, padding, state.arena.width - padding);
    let y = randomBetween(state, padding, state.arena.height - padding);
    if (edge === 0) y = padding;
    if (edge === 1) x = state.arena.width - padding;
    if (edge === 2) y = state.arena.height - padding;
    if (edge === 3) x = padding;
    const distance =
      (x - state.player.position.x) ** 2 +
      (y - state.player.position.y) ** 2;
    if (distance >= 260 ** 2) return { x, y };
  }
  return {
    x:
      state.player.position.x < state.arena.width / 2
        ? state.arena.width - padding
        : padding,
    y: clamp(
      state.player.position.y + state.arena.height * 0.32,
      padding,
      state.arena.height - padding,
    ),
  };
}

function createEnemy(
  state: BattleState,
  kind: EnemyKind,
  config: BattleConfig,
  groupAnchor?: Vec2,
): Enemy {
  const definition = ENEMY_BY_ID.get(kind);
  if (!definition) throw new Error(`Unknown enemy kind: ${kind}`);
  const padding = definition.radius + 12;
  const anchor = groupAnchor ?? createSpawnAnchor(state);
  const x = clamp(
    anchor.x + randomBetween(state, -54, 54),
    padding,
    state.arena.width - padding,
  );
  const y = clamp(
    anchor.y + randomBetween(state, -54, 54),
    padding,
    state.arena.height - padding,
  );

  const scaling = enemyWaveScaling(kind);
  const endlessScale =
    state.wave > 20 ? Math.pow(1.13, state.wave - 20) : 1;
  const dangerHealth = [1, 1, 1, 1.12, 1.26, 1.4][state.dangerLevel] ?? 1;
  const dangerDamage = [1, 1, 1, 1.12, 1.26, 1.4][state.dangerLevel] ?? 1;
  const health = Math.round(
    (definition.health + Math.max(0, state.wave - 1) * scaling.healthPerWave) *
      endlessScale *
      dangerHealth *
      config.enemyHealthMultiplier,
  );
  return {
    id: state.nextEntityId++,
    kind,
    position: { x, y },
    velocity: { x: 0, y: 0 },
    radius: definition.radius,
    health,
    maxHealth: health,
    speed: definition.speed * config.enemySpeedMultiplier,
    contactDamage:
      (definition.damage +
        Math.max(0, state.wave - 1) * scaling.damagePerWave) *
      dangerDamage,
    materialValue: definition.materialValue,
    hitFlashTicks: 0,
    aiTicks: Math.floor(nextRandom(state) * 180),
  };
}

function spawnSpecialEnemy(
  state: BattleState,
  config: BattleConfig,
  events: BattleEvent[],
): void {
  if (state.specialEnemySpawned) return;

  let kind: EnemyKind | null = null;
  const challenge = challengeTypeForWave(
    state.seed,
    state.dangerLevel,
    state.wave,
  );
  if (state.wave === 20) kind = "clockwork-chef";
  else if (state.wave > 20 && state.wave % 5 === 0) kind = "the-breakfast";
  else if (challenge === "elite") {
    kind =
      state.wave <= 12
        ? "red-captain"
        : state.wave <= 15
          ? "void-cook"
          : "iron-maw";
  }

  if (!kind) return;
  const definition = ENEMY_BY_ID.get(kind);
  if (!definition) return;
  state.enemies.push(createEnemy(state, kind, config));
  state.specialEnemySpawned = true;
  if (definition.rank === "boss") {
    events.push({ type: "boss-spawned", name: definition.name });
  }
}

function spawnTrees(state: BattleState, config: BattleConfig): void {
  state.treeSpawnTicks -= 1;
  if (state.treeSpawnTicks > 0 || state.enemies.length >= MAX_ENEMIES) return;
  const extraTrees = itemEffectTotal(state, "tree-spawns");
  const treeCount = Math.min(5, 1 + extraTrees);
  for (let index = 0; index < treeCount; index += 1) {
    if (state.enemies.length >= MAX_ENEMIES) break;
    state.enemies.push(createEnemy(state, "tree", config));
  }
  state.treeSpawnTicks = Math.max(180, 600 - extraTrees * 75);
}

function fireWeapons(state: BattleState, events: BattleEvent[]): void {
  const target = nearestEnemy(state);
  if (!target) return;
  const shellBonuses = getShellBonuses(state);

  const baseAngle = Math.atan2(
    target.position.y - state.player.position.y,
    target.position.x - state.player.position.x,
  );
  for (const weapon of state.weapons) {
    weapon.cooldownTicks -= 1;
    if (
      weapon.cooldownTicks > 0 ||
      state.projectiles.length >= MAX_PROJECTILES
    ) {
      continue;
    }
    const definition = WEAPON_BY_ID.get(weapon.definitionId);
    if (!definition) continue;

    const tierPower = 1 + (weapon.tier - 1) * 0.58;
    const projectileCount = Math.min(
      16,
      definition.projectileCount + state.player.bonusProjectiles,
    );
    for (
      let projectileIndex = 0;
      projectileIndex < projectileCount;
      projectileIndex += 1
    ) {
      if (state.projectiles.length >= MAX_PROJECTILES) break;
      const angleOffset =
        projectileCount === 1
          ? 0
          : definition.spreadRadians >= 6
            ? (projectileIndex / projectileCount) * Math.PI * 2
            : ((projectileIndex / (projectileCount - 1)) - 0.5) *
              definition.spreadRadians;
      const angle = baseAngle + angleOffset;
      const isMelee = definition.behavior === "melee";
      const isElemental = definition.family === "elemental";
      const flatDamage =
        definition.behavior === "structure"
          ? state.player.engineering
          : isMelee
            ? state.player.meleeDamage
            : isElemental
              ? state.player.elementalDamage
              : state.player.rangedDamage;
      const lowHealthBonus =
        state.player.health / Math.max(1, state.player.maxHealth) <= 0.35
          ? itemEffectTotal(state, "low-health-damage") / 100
          : 0;
      const critical = nextRandom(state) < state.player.critChance;
      if (critical) {
        events.push({
          type: "critical-hit",
          x: target.position.x,
          y: target.position.y,
        });
      }
      const projectileSpeed = isMelee
        ? Math.max(260, definition.projectileSpeed * 0.7)
        : definition.projectileSpeed * state.player.projectileSpeedMultiplier;
      state.projectiles.push({
        id: state.nextEntityId++,
        position: { ...state.player.position },
        velocity: {
          x: Math.cos(angle) * projectileSpeed,
          y: Math.sin(angle) * projectileSpeed,
        },
        radius: definition.projectileRadius * (isMelee ? 1.65 : 1),
        damage:
          (definition.damage * tierPower + flatDamage) *
          state.player.damageMultiplier *
          shellBonuses.damageMultiplier *
          (1 + lowHealthBonus) *
          (critical ? 2 : 1),
        color: definition.color,
        shape: definition.shape,
        lifetimeTicks: isMelee
          ? Math.max(10, Math.round((definition.projectileLifetimeTicks + state.player.range * 0.08) * 0.6))
          : Math.max(12, Math.round(definition.projectileLifetimeTicks + state.player.range * 0.12)),
        remainingPierces:
          definition.pierces + Math.max(0, weapon.tier - 2) + (isMelee ? 5 : 0),
        remainingBounces:
          definition.behavior === "bouncing" ? Math.min(4, weapon.tier) : 0,
        explosionRadius:
          definition.behavior === "explosive"
            ? 55 + weapon.tier * 14
            : 0,
        behavior: definition.behavior,
        hitEnemyIds: [],
      });
    }
    weapon.cooldownTicks = Math.max(
      3,
      Math.round(
        definition.intervalTicks /
          (state.player.attackSpeedMultiplier *
            shellBonuses.attackSpeedMultiplier *
            (1 + (weapon.tier - 1) * 0.08)),
      ),
    );
    events.push({
      type: "shot",
      x: state.player.position.x,
      y: state.player.position.y,
    });
  }
}

function nearestEnemy(state: BattleState): Enemy | undefined {
  let result: Enemy | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const enemy of state.enemies) {
    const distance = squaredDistance(state.player.position, enemy.position);
    if (distance < bestDistance) {
      bestDistance = distance;
      result = enemy;
    }
  }
  return result;
}

function nearestUnhitEnemy(
  state: BattleState,
  projectile: BattleState["projectiles"][number],
): Enemy | undefined {
  let result: Enemy | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const enemy of state.enemies) {
    if (enemy.health <= 0 || projectile.hitEnemyIds.includes(enemy.id)) continue;
    const distance = squaredDistance(projectile.position, enemy.position);
    if (distance < bestDistance) {
      bestDistance = distance;
      result = enemy;
    }
  }
  return result;
}

function updateProjectiles(state: BattleState): void {
  for (const projectile of state.projectiles) {
    projectile.position.x += projectile.velocity.x * TICK_SECONDS;
    projectile.position.y += projectile.velocity.y * TICK_SECONDS;
    projectile.lifetimeTicks -= 1;
  }
  state.projectiles = state.projectiles.filter(
    (projectile) =>
      projectile.lifetimeTicks > 0 &&
      projectile.position.x > -50 &&
      projectile.position.y > -50 &&
      projectile.position.x < state.arena.width + 50 &&
      projectile.position.y < state.arena.height + 50,
  );
}

function updateEnemies(state: BattleState): void {
  for (const enemy of state.enemies) {
    const definition = ENEMY_BY_ID.get(enemy.kind);
    if (!definition) continue;
    enemy.aiTicks += 1;
    if (definition.behavior === "stationary") {
      enemy.velocity.x = 0;
      enemy.velocity.y = 0;
      continue;
    }
    const direction = normalized({
      x: state.player.position.x - enemy.position.x,
      y: state.player.position.y - enemy.position.y,
    });
    let tangent = 0;
    let speedMultiplier = 1;
    if (definition.behavior === "zigzag") {
      tangent = Math.sin((enemy.aiTicks + enemy.id) * 0.09) * 0.62;
    } else if (definition.behavior === "orbit") {
      tangent = 0.72;
      speedMultiplier = 1.05;
    } else if (definition.behavior === "charge") {
      const cycle = enemy.aiTicks % 150;
      speedMultiplier = cycle > 105 ? 2.35 : cycle < 42 ? 0.46 : 0.82;
    } else if (definition.behavior === "drift") {
      tangent = Math.sin((enemy.aiTicks + enemy.id) * 0.035) * 0.4;
      speedMultiplier = 0.88;
    } else if (definition.behavior === "pulse") {
      tangent = Math.sin(enemy.aiTicks * 0.025) * 0.28;
      speedMultiplier = 0.72 + (Math.sin(enemy.aiTicks * 0.055) + 1) * 0.34;
    }
    enemy.velocity.x =
      (direction.x - direction.y * tangent) * enemy.speed * speedMultiplier;
    enemy.velocity.y =
      (direction.y + direction.x * tangent) * enemy.speed * speedMultiplier;
    enemy.position.x += enemy.velocity.x * TICK_SECONDS;
    enemy.position.y += enemy.velocity.y * TICK_SECONDS;
  }
}

function resolveProjectileHits(
  state: BattleState,
  events: BattleEvent[],
): void {
  const removedProjectiles = new Set<number>();
  const killedEnemies = new Set<number>();

  for (const projectile of state.projectiles) {
    for (const enemy of state.enemies) {
      if (
        killedEnemies.has(enemy.id) ||
        projectile.hitEnemyIds.includes(enemy.id) ||
        !circlesOverlap(projectile, enemy)
      ) {
        continue;
      }

      projectile.hitEnemyIds.push(enemy.id);
      enemy.health -= projectile.damage;
      if (state.player.knockback > 0 && enemy.kind !== "tree") {
        const direction = normalized(projectile.velocity);
        const resistance = ENEMY_BY_ID.get(enemy.kind)?.rank === "normal" ? 1 : 0.35;
        enemy.position.x = clamp(
          enemy.position.x + direction.x * state.player.knockback * resistance,
          enemy.radius,
          state.arena.width - enemy.radius,
        );
        enemy.position.y = clamp(
          enemy.position.y + direction.y * state.player.knockback * resistance,
          enemy.radius,
          state.arena.height - enemy.radius,
        );
      }
      enemy.hitFlashTicks = 4;
      if (
        state.player.lifeStealChance > 0 &&
        nextRandom(state) < state.player.lifeStealChance
      ) {
        state.player.health = Math.min(
          state.player.maxHealth,
          state.player.health + 1,
        );
      }
      events.push({
        type: "enemy-hit",
        x: enemy.position.x,
        y: enemy.position.y,
        damage: projectile.damage,
      });

      if (enemy.health <= 0) {
        killedEnemies.add(enemy.id);
        if (enemy.kind !== "tree") state.player.kills += 1;
        dropEnemyRewards(state, enemy);
        applyOnKillEffects(state);
        damageNearbyEnemyOnDeath(state, enemy, killedEnemies, events);
        events.push({
          type: "enemy-killed",
          x: enemy.position.x,
          y: enemy.position.y,
          kind: enemy.kind,
        });
      }

      if (projectile.explosionRadius > 0) {
        for (const nearby of state.enemies) {
          if (
            nearby.id === enemy.id ||
            killedEnemies.has(nearby.id) ||
            squaredDistance(nearby.position, enemy.position) >
              projectile.explosionRadius * projectile.explosionRadius
          ) {
            continue;
          }
          nearby.health -= projectile.damage * 0.45;
          if (nearby.health <= 0) {
            killedEnemies.add(nearby.id);
            if (nearby.kind !== "tree") state.player.kills += 1;
            dropEnemyRewards(state, nearby);
            applyOnKillEffects(state);
            damageNearbyEnemyOnDeath(state, nearby, killedEnemies, events);
            events.push({
              type: "enemy-killed",
              x: nearby.position.x,
              y: nearby.position.y,
              kind: nearby.kind,
            });
          }
        }
      }

      if (projectile.remainingPierces <= 0) {
        if (projectile.remainingBounces > 0) {
          const nextTarget = nearestUnhitEnemy(state, projectile);
          if (nextTarget) {
            const direction = normalized({
              x: nextTarget.position.x - projectile.position.x,
              y: nextTarget.position.y - projectile.position.y,
            });
            const speed = Math.max(260, Math.hypot(projectile.velocity.x, projectile.velocity.y));
            projectile.velocity.x = direction.x * speed;
            projectile.velocity.y = direction.y * speed;
            projectile.remainingBounces -= 1;
            projectile.remainingPierces = 0;
            break;
          }
        }
        removedProjectiles.add(projectile.id);
        break;
      }
      projectile.remainingPierces -= 1;
    }
  }

  state.enemies = state.enemies.filter((enemy) => !killedEnemies.has(enemy.id));
  state.projectiles = state.projectiles.filter(
    (projectile) => !removedProjectiles.has(projectile.id),
  );
}

function createPickup(
  state: BattleState,
  enemy: Enemy,
  kind: Pickup["kind"] = "material",
  value = enemy.materialValue,
): Pickup {
  return {
    id: state.nextEntityId++,
    position: { ...enemy.position },
    velocity: { x: 0, y: 0 },
    radius: 10,
    kind,
    value,
  };
}

function dropEnemyRewards(state: BattleState, enemy: Enemy): void {
  if (state.pickups.length < MAX_PICKUPS) {
    let materialValue = enemy.materialValue;
    if (state.baggedMaterials > 0 && enemy.kind !== "tree") {
      const bonus = Math.min(materialValue, state.baggedMaterials);
      materialValue += bonus;
      state.baggedMaterials -= bonus;
    }
    state.pickups.push(createPickup(state, enemy, "material", materialValue));
  }
  if (state.pickups.length >= MAX_PICKUPS) return;
  const definition = ENEMY_BY_ID.get(enemy.kind);
  const guaranteedCrate = definition?.rank === "elite" || definition?.rank === "boss";
  const luckFactor = Math.max(0, 1 + state.player.luck / 100);
  const consumableChance = enemy.kind === "tree" ? 1 : Math.min(0.35, 0.035 * luckFactor);
  if (guaranteedCrate || nextRandom(state) < consumableChance * 0.2 * luckFactor) {
    state.pickups.push(createPickup(state, enemy, "crate", guaranteedCrate ? 4 : 1));
  } else if (nextRandom(state) < consumableChance) {
    state.pickups.push(createPickup(state, enemy, "consumable", 3));
  }
}

function applyOnKillEffects(state: BattleState): void {
  for (const itemId of state.items) {
    const effect = ITEM_BY_ID.get(itemId)?.effect;
    if (!effect) continue;
    if (
      effect.type === "material-on-kill" &&
      nextRandom(state) < effect.chance
    ) {
      state.player.materials += effect.amount;
    }
  }
}

function damageNearbyEnemyOnDeath(
  state: BattleState,
  origin: Enemy,
  killedEnemies: Set<number>,
  events: BattleEvent[],
): void {
  const damage = itemEffectTotal(state, "damage-on-enemy-death");
  if (damage <= 0) return;
  const target = state.enemies
    .filter(
      (enemy) =>
        enemy.id !== origin.id &&
        enemy.kind !== "tree" &&
        !killedEnemies.has(enemy.id),
    )
    .sort(
      (first, second) =>
        squaredDistance(first.position, origin.position) -
        squaredDistance(second.position, origin.position),
    )[0];
  if (!target) return;
  target.health -= damage;
  events.push({
    type: "enemy-hit",
    x: target.position.x,
    y: target.position.y,
    damage,
  });
  if (target.health <= 0) {
    killedEnemies.add(target.id);
    state.player.kills += 1;
    dropEnemyRewards(state, target);
    applyOnKillEffects(state);
    events.push({
      type: "enemy-killed",
      x: target.position.x,
      y: target.position.y,
      kind: target.kind,
    });
    damageNearbyEnemyOnDeath(state, target, killedEnemies, events);
  }
}

function resolveEnemyContact(
  state: BattleState,
  config: BattleConfig,
  events: BattleEvent[],
): void {
  if (state.player.invulnerableTicks > 0) return;
  for (const enemy of state.enemies) {
    if (enemy.kind === "tree") continue;
    if (!circlesOverlap(state.player, enemy)) continue;
    if (nextRandom(state) < state.player.dodgeChance) {
      state.player.invulnerableTicks = 16;
      return;
    }

    const rawDamage = enemy.contactDamage * config.enemyDamageMultiplier;
    const effectiveArmor = state.player.armor + getShellBonuses(state).armor;
    const damage = Math.max(
      1,
      Math.round(rawDamage * (100 / (100 + Math.max(-50, effectiveArmor)))),
    );
    state.player.health -= damage;
    state.player.invulnerableTicks = 34;
    const push = normalized({
      x: state.player.position.x - enemy.position.x,
      y: state.player.position.y - enemy.position.y,
    });
    state.player.position.x = clamp(
      state.player.position.x + push.x * 30,
      state.player.radius,
      state.arena.width - state.player.radius,
    );
    state.player.position.y = clamp(
      state.player.position.y + push.y * 30,
      state.player.radius,
      state.arena.height - state.player.radius,
    );
    events.push({ type: "player-hurt", damage });
    return;
  }
}

function updatePickups(state: BattleState, events: BattleEvent[]): void {
  const collected = new Set<number>();
  for (const pickup of state.pickups) {
    const distanceSquared = squaredDistance(
      pickup.position,
      state.player.position,
    );
    if (
      distanceSquared <
      state.player.pickupRadius * state.player.pickupRadius
    ) {
      const direction = normalized({
        x: state.player.position.x - pickup.position.x,
        y: state.player.position.y - pickup.position.y,
      });
      const speed = distanceSquared < 60 * 60 ? 780 : 430;
      pickup.velocity.x = direction.x * speed;
      pickup.velocity.y = direction.y * speed;
    }
    pickup.position.x += pickup.velocity.x * TICK_SECONDS;
    pickup.position.y += pickup.velocity.y * TICK_SECONDS;

    if (circlesOverlap(pickup, state.player)) {
      collected.add(pickup.id);
      if (pickup.kind === "material") {
        state.player.materials += pickup.value;
        state.player.materialsCollected += pickup.value;
        state.player.experience += pickup.value;
        events.push({ type: "pickup", value: pickup.value });
        damageRandomEnemyFromPickup(state, events);
        processLevelUps(state, events);
      } else if (pickup.kind === "consumable") {
        const bonusHealing = itemEffectTotal(state, "heal-on-pickup");
        state.player.health = Math.min(
          state.player.maxHealth,
          state.player.health + pickup.value + bonusHealing,
        );
      } else {
        const reward = generateLootReward(state, pickup.value >= 4);
        if (reward) state.lootQueue.push(reward);
        else {
          collectMaterials(state, pickup.value, events);
          processLevelUps(state, events);
        }
      }
    }
  }
  state.pickups = state.pickups.filter((pickup) => !collected.has(pickup.id));
}

function damageRandomEnemyFromPickup(
  state: BattleState,
  events: BattleEvent[],
): void {
  const damage = itemEffectTotal(state, "damage-on-pickup");
  if (damage <= 0 || state.enemies.length === 0) return;
  const target = state.enemies[Math.floor(nextRandom(state) * state.enemies.length)];
  if (!target || target.kind === "tree") return;
  target.health -= damage;
  events.push({
    type: "enemy-hit",
    x: target.position.x,
    y: target.position.y,
    damage,
  });
  if (target.health <= 0) {
    const killedEnemies = new Set([target.id]);
    state.player.kills += 1;
    dropEnemyRewards(state, target);
    applyOnKillEffects(state);
    events.push({
      type: "enemy-killed",
      x: target.position.x,
      y: target.position.y,
      kind: target.kind,
    });
    damageNearbyEnemyOnDeath(state, target, killedEnemies, events);
    state.enemies = state.enemies.filter(
      (enemy) => !killedEnemies.has(enemy.id),
    );
  }
}

function processLevelUps(state: BattleState, events: BattleEvent[]): void {
  while (state.player.experience >= state.player.experienceToNext) {
    state.player.experience -= state.player.experienceToNext;
    state.player.level += 1;
    state.pendingLevelUps += 1;
    state.player.experienceToNext = Math.round(
      state.player.experienceToNext * 1.28 + 5,
    );
    state.player.maxHealth += 1;
    state.player.health += 1;
    events.push({ type: "level-up", level: state.player.level });
  }
}

function regeneratePlayer(state: BattleState): void {
  if (
    state.player.regenerationPerSecond <= 0 ||
    state.player.health >= state.player.maxHealth
  ) {
    return;
  }
  state.player.regenerationProgress +=
    state.player.regenerationPerSecond * TICK_SECONDS;
  const healing = Math.floor(state.player.regenerationProgress);
  if (healing <= 0) return;
  state.player.regenerationProgress -= healing;
  state.player.health = Math.min(
    state.player.maxHealth,
    state.player.health + healing,
  );
}

function finishWave(state: BattleState, events: BattleEvent[]): void {
  events.push({ type: "wave-cleared", wave: state.wave });
  for (const pickup of state.pickups) {
    if (pickup.kind === "material") {
      state.baggedMaterials += pickup.value;
    } else if (pickup.kind === "consumable") {
      state.player.health = Math.min(
        state.player.maxHealth,
        state.player.health + pickup.value + itemEffectTotal(state, "heal-on-pickup"),
      );
    } else {
      const reward = generateLootReward(state, pickup.value >= 4);
      if (reward) state.lootQueue.push(reward);
      else collectMaterials(state, pickup.value, events);
    }
  }
  state.pickups = [];
  state.enemies = [];
  state.projectiles = [];

  if (state.player.harvesting > 0) {
    const harvest = Math.floor(state.player.harvesting);
    state.player.materials += harvest;
    state.player.materialsCollected += harvest;
    state.player.experience += harvest;
    const growth = itemEffectTotal(state, "harvesting-growth");
    if (growth > 0) {
      state.player.harvesting += Math.ceil(state.player.harvesting * growth);
    }
  }
  processLevelUps(state, events);
  if (state.wave >= 20 && !state.endless) {
    state.lootQueue = [];
    state.pendingLevelUps = 0;
    state.upgradeChoices = [];
    state.phase = "victory";
    events.push({ type: "phase-change", phase: "victory" });
    return;
  }
  advancePostWave(state, events);
}

function advancePostWave(state: BattleState, events: BattleEvent[]): void {
  if (state.lootQueue.length > 0) {
    state.phase = "loot";
    events.push({ type: "phase-change", phase: "loot" });
    return;
  }
  if (state.pendingLevelUps > 0) {
    state.phase = "levelup";
    state.upgradeChoices = generateUpgradeOffers(state);
    state.upgradeRefreshes = 0;
    events.push({ type: "phase-change", phase: "levelup" });
  } else {
    openShop(state);
    events.push({ type: "phase-change", phase: "shop" });
  }
}

export function resolveLootReward(
  state: BattleState,
  rewardId: number,
  take: boolean,
): BattleEvent[] {
  if (state.phase !== "loot") return [];
  const rewardIndex = state.lootQueue.findIndex((entry) => entry.id === rewardId);
  const reward = state.lootQueue[rewardIndex];
  if (!reward) return [];
  const definition = ITEM_BY_ID.get(reward.definitionId);
  if (!definition) return [];
  const events: BattleEvent[] = [];
  if (take && canTakeItem(state, definition.id)) {
    state.items.push(definition.id);
    applyModifiers(state, definition.modifiers);
    events.push({ type: "purchase", name: definition.name });
  } else {
    const value = Math.max(1, Math.floor(definition.basePrice * 0.25));
    state.player.materials += value;
  }
  state.lootQueue.splice(rewardIndex, 1);
  if (state.lootQueue.length === 0) advancePostWave(state, events);
  return events;
}

function openShop(state: BattleState): void {
  state.phase = "shop";
  state.shopRefreshes = 0;
  if (itemEffectTotal(state, "weapon-upgrade-on-shop") > 0) {
    const candidates = state.weapons.filter((weapon) => weapon.tier < 4);
    const weapon = candidates[Math.floor(nextRandom(state) * candidates.length)];
    if (weapon) weapon.tier = Math.min(4, weapon.tier + 1);
  }
  generateShopOffers(state, false);
}

function generateShopOffers(
  state: BattleState,
  _preserveLocked: boolean,
): void {
  const locked = state.shopOffers.filter((offer) => offer.locked && !offer.sold);
  const offers: ShopOffer[] = [...locked];
  const targetWeapons = state.wave <= 2 ? 2 : state.wave <= 5 ? 1 : 0;

  while (offers.length < 4) {
    const weaponCount = offers.filter((offer) => offer.kind === "weapon").length;
    const slotsRemaining = 4 - offers.length;
    const requiredWeapons = Math.max(0, targetWeapons - weaponCount);
    const offerWeapon = requiredWeapons >= slotsRemaining || nextRandom(state) < 0.35;
    if (offerWeapon) {
      const definition = chooseShopWeapon(state);
      if (!definition) continue;
      const tier = rollTier(state, false);
      if (
        offers.some(
          (offer) =>
            offer.kind === "weapon" &&
            offer.definitionId === definition.id &&
            offer.tier === tier,
        )
      ) continue;
      offers.push({
        id: state.nextEntityId++,
        kind: "weapon",
        definitionId: definition.id,
        tier,
        price: Math.round(
          definition.basePrice * Math.pow(2, tier - 1) * (1 + state.wave * 0.055),
        ),
        sold: false,
        locked: false,
      });
    } else {
      const targetTier = rollTier(state, false);
      const definition = chooseShopItem(state, targetTier, offers);
      if (!definition) continue;
      offers.push({
        id: state.nextEntityId++,
        kind: "item",
        definitionId: definition.id,
        tier: definition.rarity,
        price: Math.round(definition.basePrice * (1 + state.wave * 0.045)),
        sold: false,
        locked: false,
      });
    }
  }
  state.shopOffers = offers.slice(0, 4);
}

function chooseShopWeapon(state: BattleState): (typeof WEAPONS)[number] | undefined {
  const available = WEAPONS.filter((entry) => state.availableWeaponIds.includes(entry.id));
  if (available.length === 0) return undefined;
  const roll = nextRandom(state);
  if (roll < 0.2 && state.weapons.length > 0) {
    const owned = state.weapons[Math.floor(nextRandom(state) * state.weapons.length)];
    const match = available.find((entry) => entry.id === owned?.definitionId);
    if (match) return match;
  }
  const earlyClassBonus = state.wave <= 5 ? (6 - state.wave) * 0.03 : 0;
  if (roll < 0.35 + earlyClassBonus && state.weapons.length > 0) {
    const families = state.weapons
      .map((weapon) => WEAPON_BY_ID.get(weapon.definitionId)?.family)
      .filter((family) => family !== undefined);
    const family = families[Math.floor(nextRandom(state) * families.length)];
    const matches = available.filter((entry) => entry.family === family);
    const match = matches[Math.floor(nextRandom(state) * matches.length)];
    if (match) return match;
  }
  return available[Math.floor(nextRandom(state) * available.length)];
}

function chooseShopItem(
  state: BattleState,
  targetTier: 1 | 2 | 3 | 4,
  offers: readonly ShopOffer[],
): (typeof ITEMS)[number] | undefined {
  const allowed = ITEMS.filter(
    (entry) =>
      state.availableItemIds.includes(entry.id) &&
      canTakeItem(state, entry.id) &&
      !offers.some((offer) => offer.kind === "item" && offer.definitionId === entry.id),
  );
  const exact = allowed.filter((entry) => entry.rarity === targetTier);
  const pool = exact.length > 0 ? exact : allowed;
  return pool[Math.floor(nextRandom(state) * pool.length)];
}

function canTakeItem(state: BattleState, definitionId: string): boolean {
  const definition = ITEM_BY_ID.get(definitionId);
  if (!definition) return false;
  const count = state.items.filter((itemId) => itemId === definitionId).length;
  return count < (definition.limit ?? Number.POSITIVE_INFINITY);
}

function applyModifiers(state: BattleState, modifiers: StatModifiers): void {
  if (modifiers.maxHealth) {
    state.player.maxHealth = Math.max(
      1,
      state.player.maxHealth + modifiers.maxHealth,
    );
    state.player.health = Math.min(
      state.player.maxHealth,
      state.player.health + Math.max(0, modifiers.maxHealth),
    );
  }
  state.player.speed = Math.max(80, state.player.speed + (modifiers.speed ?? 0));
  state.player.armor += modifiers.armor ?? 0;
  state.player.meleeDamage += modifiers.meleeDamage ?? 0;
  state.player.rangedDamage += modifiers.rangedDamage ?? 0;
  state.player.elementalDamage += modifiers.elementalDamage ?? 0;
  state.player.damageMultiplier = Math.max(
    0.2,
    state.player.damageMultiplier + (modifiers.damagePercent ?? 0) / 100,
  );
  state.player.attackSpeedMultiplier = Math.max(
    0.25,
    state.player.attackSpeedMultiplier +
      (modifiers.attackSpeedPercent ?? 0) / 100,
  );
  state.player.critChance = Math.min(
    1,
    Math.max(
      0,
      state.player.critChance + (modifiers.critChancePercent ?? 0) / 100,
    ),
  );
  state.player.lifeStealChance = Math.min(
    1,
    Math.max(
      0,
      state.player.lifeStealChance + (modifiers.lifeStealPercent ?? 0) / 100,
    ),
  );
  state.player.projectileSpeedMultiplier = Math.max(
    0.3,
    state.player.projectileSpeedMultiplier +
      (modifiers.projectileSpeedPercent ?? 0) / 100,
  );
  state.player.pickupRadius = Math.max(
    60,
    state.player.pickupRadius + (modifiers.pickupRadius ?? 0),
  );
  state.player.range += modifiers.range ?? 0;
  state.player.dodgeChance = Math.min(
    0.6,
    Math.max(
      0,
      state.player.dodgeChance + (modifiers.dodgePercent ?? 0) / 100,
    ),
  );
  state.player.regenerationPerSecond = Math.max(
    0,
    state.player.regenerationPerSecond +
      (modifiers.regenerationPerSecond ?? 0),
  );
  state.player.luck += modifiers.luck ?? 0;
  state.player.harvesting = Math.max(
    0,
    state.player.harvesting + (modifiers.harvesting ?? 0),
  );
  state.player.engineering += modifiers.engineering ?? 0;
  state.player.knockback += modifiers.knockback ?? 0;
}

function canTakeWeapon(
  state: BattleState,
  definitionId: string,
  tier: number,
): boolean {
  if (state.weapons.length < state.player.weaponSlotLimit) return true;
  return state.weapons.some(
    (weapon) =>
      weapon.definitionId === definitionId &&
      weapon.tier === tier &&
      weapon.tier < 4,
  );
}

function mergePurchasedWeapon(
  state: BattleState,
  definitionId: string,
  tier: number,
): WeaponInstance | null {
  if (tier >= 4) return null;
  const match = state.weapons.find(
    (weapon) =>
      weapon.definitionId === definitionId && weapon.tier === tier,
  );
  if (!match) return null;
  match.tier = (tier + 1) as WeaponInstance["tier"];
  match.cooldownTicks = Math.min(match.cooldownTicks, 12);
  return match;
}

function generateUpgradeOffers(state: BattleState): UpgradeOffer[] {
  return chooseDistinct(
    state,
    UPGRADES.map((entry) => entry.id),
    4,
  ).map((definitionId) => ({
    id: state.nextEntityId++,
    definitionId,
    tier: rollTier(state, true),
  }));
}

function rollTier(
  state: BattleState,
  forUpgrade: boolean,
): 1 | 2 | 3 | 4 {
  const progress = forUpgrade ? state.player.level : state.wave;
  const luckShift = Math.max(-0.15, Math.min(0.3, state.player.luck / 400));
  let weights: [number, number, number, number];
  if (progress < 4) weights = [0.94, 0.06, 0, 0];
  else if (progress < 8) weights = [0.72, 0.24, 0.04, 0];
  else if (progress < 14) weights = [0.48, 0.36, 0.14, 0.02];
  else weights = [0.3, 0.39, 0.25, 0.06];

  weights[0] = Math.max(0.04, weights[0] - luckShift);
  weights[2] += luckShift * 0.65;
  weights[3] += luckShift * 0.35;
  if (!forUpgrade) {
    if (state.wave < 2) weights[1] = 0;
    if (state.wave < 4) weights[2] = 0;
    if (state.wave < 8) weights[3] = 0;
  }

  const total = weights.reduce((sum, value) => sum + value, 0);
  let roll = nextRandom(state) * total;
  for (let index = 0; index < weights.length; index += 1) {
    roll -= weights[index] ?? 0;
    if (roll <= 0) return (index + 1) as 1 | 2 | 3 | 4;
  }
  return 1;
}

function upgradeTierPower(tier: 1 | 2 | 3 | 4): number {
  return [1, 1.55, 2.25, 3.25][tier - 1] ?? 1;
}

function scaleModifiers(
  modifiers: StatModifiers,
  multiplier: number,
): StatModifiers {
  return Object.fromEntries(
    Object.entries(modifiers).map(([key, value]) => [
      key,
      Math.round((value as number) * multiplier * 100) / 100,
    ]),
  ) as StatModifiers;
}

function itemEffectTotal(
  state: BattleState,
  effectType: NonNullable<(typeof ITEMS)[number]["effect"]>["type"],
): number {
  let total = 0;
  for (const itemId of state.items) {
    const effect = ITEM_BY_ID.get(itemId)?.effect;
    if (!effect || effect.type !== effectType) continue;
    if ("amount" in effect) total += effect.amount;
    else if ("damage" in effect) total += effect.damage;
    else if ("percent" in effect) total += effect.percent;
    else total += 1;
  }
  return total;
}

function syncWeaponClassBonuses(state: BattleState): void {
  applyModifiers(state, scaleModifiers(state.weaponClassBonuses, -1));
  const counts = new Map<string, number>();
  for (const weapon of state.weapons) {
    const family = WEAPON_BY_ID.get(weapon.definitionId)?.family;
    if (family) counts.set(family, (counts.get(family) ?? 0) + 1);
  }
  const bonusSteps = (family: string): number =>
    Math.max(0, (counts.get(family) ?? 0) - 1);
  const bonuses: StatModifiers = {
    critChancePercent: bonusSteps("precise") * 3,
    attackSpeedPercent: bonusSteps("rapid") * 5,
    damagePercent: bonusSteps("heavy") * 5,
    elementalDamage: bonusSteps("elemental"),
    armor: bonusSteps("kitchen"),
    luck: bonusSteps("weird") * 5,
  };
  state.weaponClassBonuses = bonuses;
  applyModifiers(state, bonuses);
}

function generateLootReward(
  state: BattleState,
  highQuality: boolean,
): LootReward | null {
  const allowed = ITEMS.filter(
    (entry) =>
      state.availableItemIds.includes(entry.id) && canQueueLootItem(state, entry.id),
  );
  const tier = highQuality ? Math.max(2, rollTier(state, false)) as 2 | 3 | 4 : rollTier(state, false);
  const exact = allowed.filter((entry) => entry.rarity === tier);
  const pool = exact.length > 0 ? exact : allowed;
  const definition = pool[Math.floor(nextRandom(state) * pool.length)];
  if (!definition) return null;
  return { id: state.nextEntityId++, definitionId: definition.id, tier: definition.rarity };
}

function collectMaterials(
  state: BattleState,
  value: number,
  events: BattleEvent[],
): void {
  state.player.materials += value;
  state.player.materialsCollected += value;
  state.player.experience += value;
  events.push({ type: "pickup", value });
}

function canQueueLootItem(state: BattleState, definitionId: string): boolean {
  const definition = ITEM_BY_ID.get(definitionId);
  if (!definition) return false;
  const owned = state.items.filter((itemId) => itemId === definitionId).length;
  const queued = state.lootQueue.filter(
    (reward) => reward.definitionId === definitionId,
  ).length;
  return owned + queued < (definition.limit ?? Number.POSITIVE_INFINITY);
}

function waveDurationTicks(wave: number, config: BattleConfig): number {
  if (
    config.dynamicWaveDuration === false ||
    config.waveSeconds !== DEFAULT_BATTLE_CONFIG.waveSeconds
  ) {
    return Math.max(1, Math.round(config.waveSeconds * TICK_RATE));
  }
  return waveDurationSeconds(wave) * TICK_RATE;
}

function chooseDistinct(
  state: BattleState,
  candidates: readonly string[],
  count: number,
): string[] {
  const pool = [...candidates];
  const result: string[] = [];
  while (pool.length > 0 && result.length < count) {
    const index = Math.floor(nextRandom(state) * pool.length);
    const [choice] = pool.splice(index, 1);
    if (choice) result.push(choice);
  }
  return result;
}

function squaredDistance(first: Vec2, second: Vec2): number {
  const x = first.x - second.x;
  const y = first.y - second.y;
  return x * x + y * y;
}

function circlesOverlap(
  first: { position: Vec2; radius: number },
  second: { position: Vec2; radius: number },
): boolean {
  const radius = first.radius + second.radius;
  return squaredDistance(first.position, second.position) <= radius * radius;
}

function normalized(vector: Vec2): Vec2 {
  const magnitude = Math.hypot(vector.x, vector.y);
  if (magnitude < 0.0001) return { x: 0, y: 0 };
  const scale = magnitude > 1 ? 1 / magnitude : 1;
  return { x: vector.x * scale, y: vector.y * scale };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
